import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, MenuToggle, MenuOverlay } from "../../layouts";
import {
  MapInstanceContext,
  WallLayer,
  LeafletOverrides,
  MapWrap,
  MapToggleButton as ToggleBtn,
  makeDungeonIconHtml,
  makeDungeonIconFarHtml,
  makePlayerIconHtml,
  makeAddressIconHtml,
  buildDungeonPopup,
  buildGlobalDungeonPopup,
  buildAddressPopup,
  StatusLine,
  MapControls,
} from "components/Map";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTheme } from "styled-components";
import { Chiclet, Icon } from "components";
import { useGeolocation, useDebounce } from "hooks";
import { useStateValue } from "providers";
import { apiFetch } from "actions/apiFetch";
import { getAdminDungeons } from "actions/api";
import { CharacterSelect, getActiveSave } from "./components/character-select";
import { CharacterPanel } from "./components/CharacterPanel";

export const PinmonkeyLobby = () => {
  const [{ session }] = useStateValue();
  const isAdmin = session?.user?.roles?.some((r) => r.slug === "admin");
  const isLoggedIn = !!session?.user;
  const {
    data: geo,
    loading: geoLoading,
    source: geoSource,
  } = useGeolocation();
  const debouncedLat = useDebounce(geo?.latitude, 1500);
  const debouncedLng = useDebounce(geo?.longitude, 1500);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const isLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);

  const [dungeons, setDungeons] = useState([]);
  const [globalDungeon, setGlobalDungeon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const [menuOpen, setMenuOpen] = useState(false);
  const [showAddresses, setShowAddresses] = useState(true);
  const [showWall, setShowWall] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const addressMarkersRef = useRef([]);
  const dungeonMarkersRef = useRef([]);
  const globalMarkerRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const geoRef = useRef(geo);
  useEffect(() => {
    geoRef.current = geo;
  }, [geo]);

  // Active character (global save) — loaded once, updated on select
  const [activeCharacter, setActiveCharacter] = useState(() => getActiveSave());
  const activeCharacterRef = useRef(activeCharacter);
  useEffect(() => {
    activeCharacterRef.current = activeCharacter;
  }, [activeCharacter]);
  // Character select modal (open/close)
  const [charSelectOpen, setCharSelectOpen] = useState(false);
  // Dungeon to navigate to after character selection (if ENTER was clicked first)
  const [pendingDungeon, setPendingDungeon] = useState(null);

  // Create Leaflet map once
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;

    const map = L.map(mapDivRef.current, {
      zoom: 16,
      center: [0, 0],
      zoomControl: true,
      attributionControl: false,
    });

    L.tileLayer(
      `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${process.env.CARTO_API_KEY}`,
      {
        subdomains: "abcd",
        maxZoom: 19,
      }
    ).addTo(map);

    mapRef.current = map;
    setMapReady(true);
    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  const playerMarkerRef = useRef(null);

  // Pan to user location and place player marker.
  // IP fix: soft-pan so map isn't stuck at [0,0] while GPS warms up (no lock).
  // GPS fix: pan and lock so subsequent EMA updates don't keep re-panning.
  // Fallback (no geolocation support): treat like GPS — lock immediately.
  useEffect(() => {
    if (!geo?.latitude || !geo?.longitude || !mapRef.current) return;
    if (geoSource === "loading") return;

    const isGps = geoSource === "gps" || geoSource === "fallback";
    if (mapInitializedRef.current) return; // locked after first GPS fix

    if (isGps) mapInitializedRef.current = true; // lock on GPS; IP stays unlocked

    mapRef.current.setView([geo.latitude, geo.longitude], 16);

    if (!playerMarkerRef.current) {
      playerMarkerRef.current = L.marker([geo.latitude, geo.longitude], {
        icon: L.divIcon({
          className: "",
          html: makePlayerIconHtml(colors),
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
        zIndexOffset: -100,
      }).addTo(mapRef.current);
    } else {
      playerMarkerRef.current.setLatLng([geo.latitude, geo.longitude]);
    }
  }, [geo?.latitude, geo?.longitude, geoSource]);

  // Fetch dungeons — admins see all, everyone else sees nearby
  useEffect(() => {
    if (geoSource === "loading") return;
    if (!debouncedLat || !debouncedLng) return;
    setLoading(true);
    setStatus("Searching for dungeons…");

    const nearbyFetch = apiFetch({ path: `/dungeons/nearby?lat=${debouncedLat}&lng=${debouncedLng}` });

    const dungeonFetch = isAdmin
      ? Promise.all([
          getAdminDungeons().then((data) =>
            (data?.dungeons?.rows ?? [])
              .filter((d) => !d.is_global)
              .map((d) => ({ ...d, distance: 0 }))
          ),
          nearbyFetch.then((data) => data?.global ?? null),
        ]).then(([dungeons, global]) => ({ dungeons, global }))
      : nearbyFetch.then((data) => ({
          dungeons: data?.dungeons ?? [],
          global: data?.global ?? null,
        }));

    dungeonFetch
      .then((result) => {
        setDungeons(result.dungeons);
        setGlobalDungeon(result.global);
        setLoading(false);
        setStatus("");
      })
      .catch(() => {
        setStatus("Could not load dungeons.");
        setLoading(false);
      });
  }, [isAdmin, geoSource, debouncedLat, debouncedLng]);

  // Place dungeon markers once dungeons are loaded
  useEffect(() => {
    dungeonMarkersRef.current.forEach((m) => m.remove());
    dungeonMarkersRef.current = [];
    if (!mapRef.current || dungeons.length === 0) return;

    const iconNear = L.divIcon({
      className: "",
      html: makeDungeonIconHtml(colors),
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -12],
    });
    const iconFar = L.divIcon({
      className: "",
      html: makeDungeonIconFarHtml(colors),
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -12],
    });

    const nav = {
      navigate,
      isLoggedInRef,
      activeCharacterRef,
      onNeedCharacter: (d) => {
        setPendingDungeon(d);
        setCharSelectOpen(true);
      },
    };

    dungeons.forEach((d) => {
      if (d.lat == null || d.lng == null) return;
      const inRange =
        d.radius == null || (d.distance != null && d.distance <= d.radius);

      const marker = L.marker([d.lat, d.lng], {
        icon: inRange ? iconNear : iconFar,
        zIndexOffset: inRange ? 500 : 400,
      })
        .addTo(mapRef.current)
        .bindPopup(buildDungeonPopup(colors, d, nav), {
          className: "dungeon-popup",
          maxWidth: 220,
        });
      dungeonMarkersRef.current.push(marker);
    });

    return () => {
      dungeonMarkersRef.current.forEach((m) => m.remove());
      dungeonMarkersRef.current = [];
    };
  }, [dungeons]);

  // Place global dungeon marker 200 yards (~183m) north of player
  useEffect(() => {
    if (globalMarkerRef.current) {
      globalMarkerRef.current.remove();
      globalMarkerRef.current = null;
    }
    const currentGeo = geoRef.current;
    if (
      !mapRef.current ||
      !globalDungeon ||
      geoLoading ||
      !currentGeo?.latitude ||
      !currentGeo?.longitude
    )
      return;

    const YARDS_200_DEG = 183 / 111320;
    const markerLat = currentGeo.latitude + YARDS_200_DEG;
    const markerLng = currentGeo.longitude;

    const icon = L.divIcon({
      className: "",
      html: makeDungeonIconHtml(colors),
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -12],
    });

    const popup = buildGlobalDungeonPopup(colors, globalDungeon, {
      navigate,
      isLoggedInRef,
      activeCharacterRef,
      onNeedCharacter: (d) => {
        setPendingDungeon(d);
        setCharSelectOpen(true);
      },
    });

    globalMarkerRef.current = L.marker([markerLat, markerLng], {
      icon,
      zIndexOffset: 600,
    })
      .addTo(mapRef.current)
      .bindPopup(popup, { className: "dungeon-popup", maxWidth: 220 });

    return () => {
      if (globalMarkerRef.current) {
        globalMarkerRef.current.remove();
        globalMarkerRef.current = null;
      }
    };
  }, [globalDungeon, geoLoading]);

  // Fetch addresses as location updates (debounced)
  useEffect(() => {
    if (!showAddresses || !debouncedLat || !debouncedLng) return;
    apiFetch({ path: `/places?lat=${debouncedLat}&lng=${debouncedLng}&limit=50` })
      .then((data) => setAddresses(data?.addresses?.rows ?? []))
      .catch(() => {});
  }, [showAddresses, debouncedLat, debouncedLng]);

  // Place or remove address markers
  useEffect(() => {
    if (!mapRef.current) return;
    addressMarkersRef.current.forEach((m) => m.remove());
    addressMarkersRef.current = [];
    if (!showAddresses) return;

    const icon = L.divIcon({
      className: "",
      html: makeAddressIconHtml(colors),
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    addresses.forEach((a) => {
      const lat = parseFloat(a.latitude);
      const lng = parseFloat(a.longitude);
      if (!lat || !lng) return;

      const popup = buildAddressPopup(colors, a, { navigate, showClaim: false });

      const marker = L.marker([lat, lng], { icon })
        .bindPopup(popup, { className: "dungeon-popup", maxWidth: 200 })
        .addTo(mapRef.current);
      addressMarkersRef.current.push(marker);
    });
  }, [showAddresses, addresses]);

  const showStatus = geoLoading ? "Locating you…" : status;

  return (
    <MapWrap>
      <LeafletOverrides />
      <div ref={mapDivRef} style={{ width: "100%", height: "100%" }} />
      {showStatus && <StatusLine>{showStatus}</StatusLine>}

      {/* ── Map controls ────────────────────────────────────────────────── */}
      <MapControls $dock="bottom">
        <ToggleBtn
          $active={showWall}
          onClick={() => setShowWall((v) => !v)}
        >
          ✏ Drawing
        </ToggleBtn>
        <ToggleBtn
          $active={showAddresses}
          onClick={() => setShowAddresses((v) => !v)}
        >
          ⌂ Addresses
        </ToggleBtn>
      </MapControls>

      {mapReady && (
        <MapInstanceContext.Provider value={mapRef.current}>
          <WallLayer active={showWall} />
        </MapInstanceContext.Provider>
      )}

      {/* ── Character panel ─────────────────────────────────────────────── */}
      <CharacterPanel
        $boldHover
        isLoggedIn={isLoggedIn}
        activeCharacter={activeCharacter}
        onLogin={() => navigate("/login")}
        onChangeCharacter={() => setCharSelectOpen(true)}
        onSelectCharacter={() => setCharSelectOpen(true)}
      />

      {/* ── Character select modal ───────────────────────────────────────── */}
      {isLoggedIn && charSelectOpen && (
        <CharacterSelect
          onEnter={(save) => {
            setActiveCharacter(save);
            setCharSelectOpen(false);
            if (pendingDungeon) {
              navigate(`/games/dungeon/${pendingDungeon.slug}`);
              setPendingDungeon(null);
            }
          }}
          onCancel={() => {
            setCharSelectOpen(false);
            setPendingDungeon(null);
          }}
        />
      )}

      {/* ── Side menu ───────────────────────────────────────────────────── */}
      <MenuToggle onClick={() => setMenuOpen((v) => !v)}>
        <Chiclet>
          <Icon icon="menu" fill={colors("foreground")} />
        </Chiclet>
        <Menu show={menuOpen} close={() => setMenuOpen(false)} />
      </MenuToggle>
      <MenuOverlay $show={menuOpen} onClick={() => setMenuOpen(false)} />
    </MapWrap>
  );
};
